import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { MetricCard, ScalrCarousel } from '@/components/customComponents'
import FilterBar from '@/components/FilterBar'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

import { metricCardColors } from '@/constants/COLOR_PALETTE'
import { toast, useCockpitMetrics } from '@/hooks'
import { addAlert, useAppDispatch, useReduxBaseFilter } from '@/store'
import { dateToString } from '@/utils'
import { addSampleAlerts } from '@/utils/alertDemo'
import { DateType } from '@/utils/date'
import {
  IconBrandSpeedtest,
  IconCashBanknote,
  IconCurrencyDollar,
  IconPercentage,
  IconStars,
} from '@tabler/icons-react'

function DashboardContent() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (!hasInitialized.current) {
      addSampleAlerts({
        addAlert: (alert: Parameters<typeof addAlert>[0]) =>
          dispatch(addAlert(alert)),
      } as any)
      hasInitialized.current = true
    }
  }, [dispatch])

  const { selectedBranch, date, dateFilterType } =
    useReduxBaseFilter('dashboard')

  const {
    companyData,
    metricCardData,
    loading,
    error: dashboardMetricsError,
  } = useCockpitMetrics(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    dateFilterType === 'test-range'
      ? 'month-to-date'
      : (dateFilterType as DateType),
    selectedBranch
  )

  useEffect(() => {
    if (dashboardMetricsError) {
      toast({
        title: t('clarity.errors.somethingWentWrong'),
        description: dashboardMetricsError.message,
        variant: 'destructive',
      })
    }
  }, [dashboardMetricsError])

  return (
    <Card className="w-full h-full">
      <CardHeader>
        <CardTitle pageTitle>{t('clarity.dashboard')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <FilterBar pageId="dashboard" />

        <div className="space-y-6">
          <div className="space-y-3 mb-10">
            <ScalrCarousel>
              <MetricCard
                title={t('clarity.metrics.adns')}
                subTitle={t('clarity.metrics.adnsSubtitle')}
                mtdCurrContent={metricCardData?.currMetricsMonthly?.adns}
                ytdCurrContent={metricCardData?.currMetricsYearly?.adns}
                mtdPrevContent={metricCardData?.prevMetricsMonthly?.adns}
                ytdPrevContent={metricCardData?.prevMetricsYearly?.adns}
                content={companyData?.adns}
                type="money"
                icon={<IconCurrencyDollar color="white" size={20} />}
                color={metricCardColors.blue500}
                loading={loading}
              />
              <MetricCard
                title={t('clarity.metrics.cogs')}
                subTitle={t('clarity.metrics.cogsSubtitle')}
                mtdCurrContent={metricCardData?.currMetricsMonthly?.cogsPti}
                ytdCurrContent={metricCardData?.currMetricsYearly?.cogsPti}
                mtdPrevContent={metricCardData?.prevMetricsMonthly?.cogsPti}
                ytdPrevContent={metricCardData?.prevMetricsYearly?.cogsPti}
                content={companyData?.cogsPti}
                type="money"
                icon={<IconCashBanknote color="white" size={20} />}
                color={metricCardColors.red500}
                loading={loading}
                isIncreasingPositive={false}
              />
              <MetricCard
                title={t('clarity.metrics.laborRatio')}
                subTitle={t('clarity.metrics.laborRatioSubtitle')}
                mtdCurrContent={metricCardData?.currMetricsMonthly?.laborRatio}
                ytdCurrContent={metricCardData?.currMetricsYearly?.laborRatio}
                mtdPrevContent={metricCardData?.prevMetricsMonthly?.laborRatio}
                ytdPrevContent={metricCardData?.prevMetricsYearly?.laborRatio}
                content={companyData?.laborRatio}
                type="percentage"
                icon={<IconPercentage color="white" size={20} />}
                color={metricCardColors.green500}
                loading={loading}
                isIncreasingPositive={false}
              />
              <MetricCard
                title={t('clarity.metrics.speedOfService')}
                subTitle={t('clarity.metrics.speedOfServiceSubtitle')}
                mtdCurrContent={
                  metricCardData?.currMetricsMonthly?.speedOfService
                }
                ytdCurrContent={
                  metricCardData?.currMetricsYearly?.speedOfService
                }
                mtdPrevContent={
                  metricCardData?.prevMetricsMonthly?.speedOfService
                }
                ytdPrevContent={
                  metricCardData?.prevMetricsYearly?.speedOfService
                }
                content={companyData?.speedOfService}
                type="number"
                icon={<IconBrandSpeedtest color="white" size={20} />}
                color={metricCardColors.orange500}
                loading={loading}
                isIncreasingPositive={false}
              />
              <MetricCard
                title={t('clarity.metrics.googleReviews')}
                subTitle={t('clarity.metrics.googleReviewsSubtitle')}
                mtdCurrContent={
                  metricCardData?.currMetricsMonthly?.guestSatisfaction
                }
                ytdCurrContent={
                  metricCardData?.currMetricsYearly?.guestSatisfaction
                }
                mtdPrevContent={
                  metricCardData?.prevMetricsMonthly?.guestSatisfaction
                }
                ytdPrevContent={
                  metricCardData?.prevMetricsYearly?.guestSatisfaction
                }
                content={companyData?.guestSatisfaction}
                type="number"
                icon={<IconStars color="white" size={20} />}
                color={metricCardColors.blue300}
                loading={loading}
              />
            </ScalrCarousel>
          </div>

          {/* <div>
            <DataTable
              columns={groupedCockpitMetricsColumns}
              data={[companyData, ...branchData].filter(
                (item): item is CockpitMetricsModel => item !== undefined
              )}
              selectedBranch={selectedBranch}
              cardTitle="Branch Metrics"
              cardDescription="General Branch Metrics"
              tableViewOption={true}
              loading={loading}
            />
          </div> */}
        </div>
      </CardContent>
    </Card>
  )
}

export default function DashboardPage() {
  return <DashboardContent />
}
